import { useState } from 'react'
import { RotateCcw, UserPlus, Users } from 'lucide-react'
import { PageHeader } from '@/components/page-header'
import { KpiCard } from '@/components/kpi-card'
import { HorizontalBarChart } from '@/components/bar-chart'
import { DataTable } from '@/components/data-table'
import { PaginationBar } from '@/components/pagination-bar'
import { DataLoadError } from '@/components/data-load-error'
import { DataUnavailable } from '@/components/data-unavailable'
import { Button } from '@/components/ui/button'
import { useAudienceData } from '@/hooks/use-audience-data'
import type { StudioReviewUnavailableReasonCode } from '@/hooks/dashboard-data-state'
import type {
  AudienceKpi,
  FanRecord,
  StudioReviewDataClient,
  TrafficSource,
} from '@/services/dashboard-api'
import { useStudioStore } from '@/store/studio-store'
import { pushStudioRoute } from '@/lib/studio-navigation'
import { DashboardContainer } from '../components/dashboard-container'
import { OperationsTabs } from '../components/operations-tabs'
import { ReviewObservationDisclosure } from '../components/review-observation-disclosure'
import { useStudioLocale } from '@/i18n'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'

type AudienceCopy = StudioDictionary['businessScreens']['audience']

/** Column headers are words on the screen, so they follow the active locale
 *  rather than being frozen at module scope. */
function fanColumnsFor(copy: AudienceCopy) {
  return [
    { key: 'name' as const, header: copy.columns.name },
    { key: 'email' as const, header: copy.columns.email },
    { key: 'source' as const, header: copy.columns.role },
    { key: 'joined' as const, header: copy.columns.joined, align: 'right' as const },
  ]
}

interface AudiencePageProps {
  dataClient?: StudioReviewDataClient
}

export function AudiencePage({ dataClient }: AudiencePageProps = {}) {
  const { kpis, sources, fans, reload } = useAudienceData(dataClient)
  const [page, setPage] = useState(1)
  const setStudioRoute = useStudioStore((state) => state.setStudioRoute)
  const { strings } = useStudioLocale()
  const copy = strings.businessScreens.audience
  const reviewCopy = strings.businessScreens.review
  const pageState = kpis.status === 'ready' ? kpis.reviewState ?? 'error' : kpis.status

  function goToShare() {
    setStudioRoute('apps-live')
    pushStudioRoute('apps-live')
  }

  let content: React.ReactNode
  switch (pageState) {
    case 'loading':
      content = (
        <div className="animate-pulse space-y-4" role="status">
          <p className="text-sm text-muted-foreground">{copy.loading}</p>
          <div className="h-48 rounded-xl bg-muted" aria-hidden="true" />
        </div>
      )
      break
    case 'measured': {
      const audienceKpis = kpis.data as AudienceKpi | null
      content = (
        <>
          <ReviewObservationDisclosure observedAt={kpis.observedAt as string} evidenceRefs={kpis.evidenceRefs} scope={copy.scope} />
          {audienceKpis !== null && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <KpiCard title={copy.kpis.reviewers} value={audienceKpis.total_fans.toLocaleString()} icon={<Users className="h-4 w-4 text-muted-foreground" />} />
              <KpiCard title={copy.kpis.new7d} value={String(audienceKpis.new_7d)} icon={<UserPlus className="h-4 w-4 text-muted-foreground" />} />
              <KpiCard title={copy.kpis.returnRate} value={`${audienceKpis.return_rate}%`} icon={<RotateCcw className="h-4 w-4 text-muted-foreground" />} />
            </div>
          )}
          <HorizontalBarChart data={sources.data as TrafficSource[]} xKey="source" yKey="percentage" title={copy.rolesTitle} />
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">{copy.recentReviewers}</h2>
            <DataTable columns={fanColumnsFor(copy)} data={fans.data as FanRecord[]} />
            <PaginationBar page={page} totalPages={1} pageSize={10} onPageChange={setPage} />
          </div>
        </>
      )
      break
    }
    case 'empty':
      content = (
        <div className="rounded-xl border border-dashed border-border bg-card p-6" role="status">
          <p className="text-sm font-medium text-foreground">{copy.empty}</p>
          <ReviewObservationDisclosure observedAt={kpis.observedAt as string} evidenceRefs={kpis.evidenceRefs} scope={copy.scope} state="empty" />
        </div>
      )
      break
    case 'unavailable': {
      const guidance = copy.unavailable[kpis.unavailableReasonCode as StudioReviewUnavailableReasonCode]
      content = (
        <>
          <DataUnavailable summary={guidance.summary} message={kpis.unavailableReason as string} testId="audience-unavailable" />
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-foreground">{guidance.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{guidance.detail}</p>
            <Button variant="outline" size="sm" className="mt-4 min-h-10" onClick={goToShare}>{reviewCopy.openSurface(strings.navigation.share)}</Button>
          </section>
        </>
      )
      break
    }
    case 'error':
      content = <DataLoadError message={copy.loadFailed} onRetry={reload} testId="audience-kpis-error" />
      break
  }

  return (
    <DashboardContainer className="py-6 space-y-6">
      <OperationsTabs activeRoute="members" />
      <PageHeader
        route="members"
        title={strings.navigation.audience}
        description={copy.description}
      />
      {content}
    </DashboardContainer>
  )
}
